import {
    ServicesAction,
    SERVICES_RESET_ACTION_TYPE,
    SERVICES_CLEAR_ACTION_TYPE,
    SERVICES_FETCH_START_ACTION_TYPE,
    SERVICES_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_FETCH_EVALUATION_START_ACTION_TYPE,
    SERVICES_FETCH_EVALUATION_SUCCESS_ACTION_TYPE,
    SERVICES_SAVE_START_ACTION_TYPE,
    SERVICES_SAVE_SUCCESS_ACTION_TYPE,
    SERVICES_DELETE_START_ACTION_TYPE,
    SERVICES_DELETE_SUCCESS_ACTION_TYPE,
    SERVICES_ACTIVATE_START_ACTION_TYPE,
    SERVICES_ACTIVATE_SUCCESS_ACTION_TYPE,
    SERVICES_INACTIVATE_START_ACTION_TYPE,
    SERVICES_INACTIVATE_SUCCESS_ACTION_TYPE,
    SERVICES_PET_TYPES_UPDATE_START_ACTION_TYPE,
    SERVICES_PET_TYPES_UPDATE_SUCCESS_ACTION_TYPE,
    SERVICES_BUSINESS_AREA_UPDATE_START_ACTION_TYPE,
    SERVICES_BUSINESS_AREA_UPDATE_SUCCESS_ACTION_TYPE,
    SERVICES_STAFF_UPDATE_START_ACTION_TYPE,
    SERVICES_STAFF_UPDATE_SUCCESS_ACTION_TYPE,
    SERVICES_DAYCARE_FETCH_START_ACTION_TYPE,
    SERVICES_DAYCARE_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_ERROR_ACTION_TYPE,
    SERVICES_BOARDING_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_BOARDING_FETCH_START_ACTION_TYPE,
    SERVICES_TRANSPORTATION_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_TRANSPORTATION_FETCH_START_ACTION_TYPE,
    SERVICES_TRAINING_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_TRAINING_FETCH_START_ACTION_TYPE,
    SERVICES_WALKING_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_WALKING_FETCH_START_ACTION_TYPE,
    SERVICES_BOARDING_OFFERS_FETCH_START_ACTION_TYPE,
    SERVICES_BOARDING_OFFERS_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_SAVE_BULK_SUCCESS_ACTION_TYPE,
    SERVICES_SAVE_BULK_START_ACTION_TYPE
} from './ServicesActionsTypes';
import { ThunkAction } from 'redux-thunk';
import Analytics from 'analytics/client';
import { Events } from 'analytics/events';
import { AxiosResponse } from 'axios';
import { ServicesState } from 'reducers/services/ServicesState';
import { BusinessAreaType, SERVICES_BUSINESS_AREAS } from 'model/Service';
import ApiClient, { createTokenConfig, isCancelled } from 'api/ApiClient';
import { NotificationsAction, showSuccess } from '@spike/notifications-action';
import { showError } from '@spike/notifications-action';
import store, { RootState } from 'store';
import { isValidBoardingOfferDateString } from 'utils/boardingOfferDates';
import { Status } from 'model';
import {
    convertToBoardingServices,
    convertToDaycareServices,
    convertToEvaluationService,
    convertToMeta,
    convertToService,
    convertToTransportationServices,
    convertToTrainingServices,
    convertToWalkingServices,
    mapServiceItemTypeToApi
} from './ServicesConverter';
import { Service, isServiceDuration, isMetadataDuration } from 'model/Service';
import {
    BoardingServiceDto,
    DaycareServiceDto,
    ServiceDto,
    ServicesPaginatedDto,
    TransportationServiceDto,
    TrainingServiceDto,
    WalkingServiceDto
} from './ServicesDtos';

const servicesUrl = '/services';
const saveBulkCreateServicesUrl = '/service/bulk_create';
const saveBulkUpdateServicesUrl = '/service/bulk_update';
const evaluationUrl = '/evaluation';
const activeServicesUrl = '/status/service';
const deleteServicesUrl = '/destroy/service';
const petTypesUpdateServicesUrl = '/pet_types/service';
const businessAreaUpdateServicesUrl = '/service/bulk_business_area';
const daycareServicesUrl = '/service/available_daycares';
const boardingServicesUrl = '/service/available_boardings';
const transportationServicesUrl = '/service/available_transportations';
const trainingServicesUrl = '/service/available_trainings';
const walkingServicesUrl = '/service/available_walkings';
const boardingOffersUrl = '/service/boarding_offers';

const currentMarketplaceServiceId = (
    service: Service,
    responseData: unknown,
    marketplaceId: number
): number | undefined => {
    if (service.id) return Number(service.id);

    const data = responseData as {
        id?: number;
        marketplace_id?: number;
        services?: Array<{ id: number; marketplace_id?: number }>;
    };

    const fromCurrentMarketplace = (data?.services || []).find(
        saved => Number(saved.marketplace_id) === Number(marketplaceId)
    );
    if (fromCurrentMarketplace?.id) return Number(fromCurrentMarketplace.id);

    if (data?.id && (data.marketplace_id == null || Number(data.marketplace_id) === Number(marketplaceId))) {
        return Number(data.id);
    }

    return undefined;
};

const syncServiceFormIds = async (service: Service, responseData: unknown): Promise<void> => {
    const formIds = Array.from(
        new Set((service.forms || []).map(form => Number(form.id)).filter(id => Number.isFinite(id) && id > 0))
    );
    if (!service.id && formIds.length === 0) return;

    const marketplaceId = store.getState().login.auth.marketplaceId;
    const serviceId = currentMarketplaceServiceId(service, responseData, marketplaceId);
    if (!serviceId || !Number.isFinite(serviceId) || serviceId <= 0) return;

    await ApiClient.patch(
        `${servicesUrl}/${serviceId}?marketplace_id=${marketplaceId}`,
        { service: { form_ids: formIds } },
        createTokenConfig(store.getState().login.auth.token!)
    );
};

export const resetServicesThunk = (): ThunkAction<void, ServicesState, null, ServicesAction> => {
    return async dispatch => {
        dispatch(clear());
    };
};

export const fetchServicesThunk = (
    duplicatedId?: number,
    page?: number,
    per?: number,
    status?: string,
    dir?: string,
    searchText?: string,
    businessArea = SERVICES_BUSINESS_AREAS.GROOMING + ',' + SERVICES_BUSINESS_AREAS.DENTAL, // Default grooming and dental filter (e.g., 'daycare', 'boarding'),
    marketplaceIds?: Array<number>
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        // Handle multi business area calls: map 'grooming,dental' to 'grooming' for Redux state
        const reduxBusinessArea = businessArea.includes(SERVICES_BUSINESS_AREAS.GROOMING)
            ? (SERVICES_BUSINESS_AREAS.GROOMING as BusinessAreaType)
            : (businessArea as BusinessAreaType);
        dispatch(fetchStart(reduxBusinessArea));

        const state = store.getState();
        const { marketplaceId, token } = state.login.auth;

        const params: Record<string, string | number> = {};

        if (page != null) params.page = page;
        if (per != null) params.per = per;
        if (status) params.status = mapStatusToIds(status);
        if (dir) params.dir = dir;
        if (searchText) params.q = searchText;

        // Build URL with optional business_area parameter
        let url = `${servicesUrl}?marketplace_id=${marketplaceId}${
            marketplaceIds ? `&marketplace_ids=${marketplaceIds.join(',')}` : ''
        }`;
        if (businessArea) {
            url += `&business_area=${businessArea}`;
        }

        try {
            const response: AxiosResponse<ServicesPaginatedDto> = await ApiClient.get(url, {
                ...createTokenConfig(token!),
                params
            });
            dispatch(fetchSuccess(response.data, duplicatedId, reduxBusinessArea));
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error fetching services.'));
            }
        }
    };
};

export const saveServiceThunk = (
    service: Service,
    changedName?: boolean,
    duplicated?: boolean,
    deletedQuestionIds?: number[],
    applyNewPriceToAppointments?: boolean
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(saveStart());

        const marketplaceId = store.getState().login.auth.marketplaceId;
        const apiClientMethod = service.id === undefined ? ApiClient.post : ApiClient.patch;
        const url =
            service.id === undefined
                ? `${servicesUrl}?marketplace_id=${marketplaceId}`
                : `${servicesUrl}/${service.id}?marketplace_id=${marketplaceId}`;

        try {
            const serviceNoName = {
                id: service.id,
                uuid: service.uuid,
                marketplace_id: marketplaceId,
                description: service.description,
                // Handle duration based on type (MetadataDuration vs ServiceDuration)
                duration_hours:
                    isServiceDuration(service.duration) && service.duration.simple
                        ? service.duration.simple.hours
                        : null,
                duration_minutes:
                    isServiceDuration(service.duration) && service.duration.simple
                        ? service.duration.simple.minutes
                        : null,
                variable_duration: isMetadataDuration(service.duration)
                    ? service.duration.metadata // MetadataDuration: already JSON string, use directly
                    : service.businessArea?.id === SERVICES_BUSINESS_AREAS.TRAINING && service.trainingData
                    ? JSON.stringify({
                          hours: service.trainingData.hours,
                          minutes: service.trainingData.minutes,
                          booking_type: service.trainingData.bookingType,
                          max_pets: service.trainingData.maxPets,
                          in_home_service: service.trainingData.inHomeService
                      }) // Training: store metadata in variable_duration
                    : service.businessArea?.id === SERVICES_BUSINESS_AREAS.WALKING && service.walkingData
                    ? JSON.stringify({
                          hours: service.walkingData.hours,
                          minutes: service.walkingData.minutes,
                          booking_type: service.walkingData.bookingType,
                          max_pets: service.walkingData.maxPets,
                          in_home_service: service.walkingData.inHomeService
                      }) // Walking: store metadata in variable_duration
                    : JSON.stringify(
                          // ServiceDuration: serialize variable duration array
                          service.duration.variable.map(vd => ({
                              size_meta_variable: vd.petSize.id,
                              hours: vd.duration.hours,
                              minutes: vd.duration.minutes
                          }))
                      ),
                includes_tax: service.pricing.taxable,
                tax_ids: service.pricing.taxes.map(tax => tax.id),
                fixed_price: service.pricing.fixedPrice
                    ? JSON.stringify(
                          service.pricing.fixedPrice.map(fp => {
                              // Transportation: All pet types have the same pickup/dropoff prices
                              if (service.businessArea?.id === SERVICES_BUSINESS_AREAS.TRANSPORTATION) {
                                  return {
                                      pet_type_id: fp.petType?.id,
                                      pickup_price: fp.transportationPrice?.pickUpPrice || 0,
                                      dropoff_price: fp.transportationPrice?.dropOffPrice || 0
                                  };
                              }
                              // Grooming/Dental/Daycare: uses price
                              return {
                                  pet_type_id: fp.petType?.id,
                                  price: fp.price
                              };
                          })
                      )
                    : null,
                variable_price:
                    service.pricing.variablePrice === undefined
                        ? null
                        : JSON.stringify({
                              variables: {
                                  pet_type_variable: service.pricing.variablePrice!.variables.petTypeVariable,
                                  pet_size_variable: service.pricing.variablePrice!.variables.petSizeVariable,
                                  hair_type_variable: service.pricing.variablePrice!.variables.hairTypeVariable,
                                  hair_length_variable: service.pricing.variablePrice!.variables.hairLengthVariable,
                                  exotic_type_variable: service.pricing.variablePrice!.variables.exoticTypeVariable
                              },
                              prices: service.pricing.variablePrice!.prices.map(vpo => ({
                                  uuid: vpo.uuid,
                                  pet_type_id: vpo.petType?.id,
                                  size_meta_variable: vpo.petSize ? vpo.petSize.id : undefined,
                                  hair_type_meta_variable: vpo.hairType ? vpo.hairType.id : undefined,
                                  hair_length_meta_variable: vpo.hairLength ? vpo.hairLength?.id : undefined,
                                  exotic_type_meta_variable: vpo.exoticType ? vpo.exoticType?.id : undefined,
                                  price: vpo.price
                              }))
                          }),
                business_area: service.businessArea?.id || '',
                pet_list: service.petTypes.map(petType => petType.id),
                staff_ids: service.staff.map(staff => staff.id),
                status_id: service.status.id,
                tag_list: service.tags,
                has_report: service.hasReport,
                house_call: service.houseCall,
                olb_enabled: service.olbEnabled,
                form_ids: Array.from(new Set((service.forms || []).map(form => form.id))),
                color: service.color,
                ...(applyNewPriceToAppointments !== undefined
                    ? { apply_new_price_to_appointments: applyNewPriceToAppointments }
                    : {}),
                evaluation_attributes: service.evaluation
                    ? {
                          id: service.evaluation?.id,
                          evaluation_expiry_enabled: service.evaluation.evaluationExpiry.enabled,
                          evaluation_expiry: service.evaluation.evaluationExpiry.enabled
                              ? service.evaluation.evaluationExpiry.duration
                              : null,
                          category_list: service.evaluation.category,
                          custom_question_enabled: service.evaluation.evaluationQuestionsEnabled,
                          custom_questions_attributes: [
                              ...(service.evaluation.evaluationQuestions?.map(q => ({
                                  id: q?.id,
                                  question_text: q.questionText,
                                  enabled: q.enabled,
                                  required: q.required,
                                  category: 'for_pets',
                                  display_order: q.displayOrder,
                                  questionable_type: q.questionableType,
                                  marketplace_id: marketplaceId,
                                  questionable_id: service.evaluation?.id
                              })) ?? []),
                              ...((deletedQuestionIds || []).map(id => ({
                                  id,
                                  _destroy: true,
                                  marketplace_id: marketplaceId
                              })) as any[])
                          ]
                      }
                    : null,
                // Daycare-specific fields (only included if present)
                ...(service.dropOffTime && {
                    drop_off_start: service.dropOffTime.start,
                    drop_off_end: service.dropOffTime.end
                }),
                ...(service.pickUpTime && {
                    pick_up_start: service.pickUpTime.start,
                    pick_up_end: service.pickUpTime.end
                }),
                // Boarding-specific fields (only included if present)
                ...(service.checkInTime && {
                    check_in_start: service.checkInTime.start,
                    check_in_end: service.checkInTime.end
                }),
                ...(service.checkOutTime && {
                    check_out_start: service.checkOutTime.start,
                    check_out_end: service.checkOutTime.end
                }),
                ...(service.businessArea?.id !== SERVICES_BUSINESS_AREAS.BOARDING && {
                    service_items_attributes: (service.serviceItems || []).map(si => ({
                        ...(si.id ? { id: si.id } : {}),
                        marketplace_id: marketplaceId,
                        item_id: si.itemID,
                        item_type: mapServiceItemTypeToApi(si.itemType),
                        business_area: si.businessArea, // Include business_area field
                        uuid: si.uuid,
                        amount: si.amount,
                        quantity: si.quantity,
                        per_stay: si.perStay,
                        _destroy: si._destroy ? si._destroy : false
                    }))
                }),
                marketplaces: service.marketplaces.map(mp => ({
                    marketplace_id: mp.marketplaceId,
                    tax_ids: mp.taxes,
                    staff_ids: mp.staffIds,
                    room_ids: mp.roomIds ?? [],
                    room_unit_ids: mp.roomUnitIds ?? [],
                    vehicle_ids: mp.vehicleIds ?? []
                }))
            };

            const serviceName = { ...serviceNoName, name: service.name };

            const serviceToSend = service.id !== undefined && changedName === false ? serviceNoName : serviceName;

            const response: AxiosResponse<ServiceDto> = await apiClientMethod(
                url,
                {
                    service: serviceToSend
                },
                createTokenConfig(store.getState().login.auth.token!)
            );
            const saved = convertToService(response.data);
            dispatch(saveSuccess(saved));
            // One-shot: CTA Saved Service while services onboarding incomplete
            try {
                const teamDone = store.getState().onboardingSteps.onboardingSteps.servicesDone; // variable naming kept
                const marketplaceId = store.getState().marketplace.marketplace?.id;
                if (!teamDone && marketplaceId) {
                    const key = `event_one_shot_${Events.SAVE_SERVICE_CLICKED}_${marketplaceId}`;
                    if (localStorage.getItem(key) !== '1') {
                        Analytics.track(Events.SAVE_SERVICE_CLICKED as any, {
                            marketplace_id: marketplaceId,
                            service_id: saved.id,
                            business_area: saved.businessArea?.id,
                            pet_type_ids: saved.petTypes.map(pt => pt.id),
                            has_variable_price: !!saved.pricing.variablePrice,
                            has_variable_duration: !isServiceDuration(saved.duration),
                            staff_count: saved.staff.length
                        });
                        localStorage.setItem(key, '1');
                    }
                }
            } catch (e) {
                // non-blocking
            }
        } catch (apiError: any) {
            if (isCancelled(apiError)) return;
            dispatch(error());
            const defaultMsg = 'Error creating service.';
            const res = apiError.response;

            if (!res) {
                dispatch(showError(defaultMsg));
                dispatch(fetchServicesThunk());
                return;
            }

            if (res.status === 422) {
                const nameErr = res.data?.name;
                const msg = (Array.isArray(nameErr) && nameErr[0] && `Service name ${nameErr[0]}`) || defaultMsg;
                dispatch(showError(msg));
                return;
            }

            // Generic error message
            dispatch(showError(defaultMsg));
            //dispatch(fetchServicesThunk());
        }
    };
};

// The API answers either { field: [messages] } or { errors: { field: [messages] } }.
// Without this the toast falls back to a generic message and hides the real reason.
const parseValidationErrors = (data: unknown): string | undefined => {
    const isRecord = (value: unknown): value is Record<string, unknown> =>
        !!value && typeof value === 'object' && !Array.isArray(value);

    if (!isRecord(data)) return undefined;

    const fields = isRecord(data.errors) ? data.errors : data;

    const messages = Object.entries(fields).flatMap(([field, value]) => {
        const list = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
        const label = field.replace(/_/g, ' ');
        return list.filter(item => typeof item === 'string').map(item => `${label} ${item}`);
    });

    if (messages.length === 0) return undefined;

    return messages.join('. ').replace(/^./, first => first.toUpperCase()) + '.';
};

export const saveBulkServicesThunk = (
    service: Service,
    changedName?: boolean,
    deletedQuestionIds?: number[],
    applyNewPriceToAppointments?: boolean
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(saveBulkStart());

        const marketplaceId = store.getState().login.auth.marketplaceId;
        const apiClientMethod = service.id === undefined ? ApiClient.post : ApiClient.patch;
        const url = `${
            service.id === undefined ? saveBulkCreateServicesUrl : saveBulkUpdateServicesUrl
        }?marketplace_id=${marketplaceId}`;

        try {
            const serviceNoName = {
                //id: service.id,
                //uuid: service.uuid,
                //marketplace_id: marketplaceId, //Commented for bulk
                sibling_uuid: service.siblingUuid,
                description: service.description,
                // Handle duration based on type (MetadataDuration vs ServiceDuration)
                duration_hours:
                    isServiceDuration(service.duration) && service.duration.simple
                        ? service.duration.simple.hours
                        : null,
                duration_minutes:
                    isServiceDuration(service.duration) && service.duration.simple
                        ? service.duration.simple.minutes
                        : null,
                variable_duration: isMetadataDuration(service.duration)
                    ? service.duration.metadata // MetadataDuration: already JSON string, use directly
                    : service.businessArea?.id === SERVICES_BUSINESS_AREAS.TRAINING && service.trainingData
                    ? JSON.stringify({
                          hours: service.trainingData.hours,
                          minutes: service.trainingData.minutes,
                          booking_type: service.trainingData.bookingType,
                          max_pets: service.trainingData.maxPets,
                          in_home_service: service.trainingData.inHomeService
                      }) // Training: store metadata in variable_duration
                    : service.businessArea?.id === SERVICES_BUSINESS_AREAS.WALKING && service.walkingData
                    ? JSON.stringify({
                          hours: service.walkingData.hours,
                          minutes: service.walkingData.minutes,
                          booking_type: service.walkingData.bookingType,
                          max_pets: service.walkingData.maxPets,
                          in_home_service: service.walkingData.inHomeService
                      }) // Walking: store metadata in variable_duration
                    : JSON.stringify(
                          // ServiceDuration: serialize variable duration array
                          service.duration.variable.map(vd => ({
                              size_meta_variable: vd.petSize.id,
                              hours: vd.duration.hours,
                              minutes: vd.duration.minutes
                          }))
                      ),
                //includes_tax: service.pricing.taxable, //Comentado por bulk
                //tax_ids: service.pricing.taxes.map(tax => tax.id), //Comentado por bulk
                fixed_price: service.pricing.fixedPrice
                    ? JSON.stringify(
                          service.pricing.fixedPrice.map(fp => {
                              // Transportation: All pet types have the same pickup/dropoff prices
                              if (service.businessArea?.id === SERVICES_BUSINESS_AREAS.TRANSPORTATION) {
                                  return {
                                      pet_type_id: fp.petType?.id,
                                      pickup_price: fp.transportationPrice?.pickUpPrice || 0,
                                      dropoff_price: fp.transportationPrice?.dropOffPrice || 0
                                  };
                              }
                              // Grooming/Dental/Daycare: uses price
                              return {
                                  pet_type_id: fp.petType?.id,
                                  price: fp.price
                              };
                          })
                      )
                    : null,
                variable_price:
                    service.pricing.variablePrice === undefined
                        ? null
                        : JSON.stringify({
                              variables: {
                                  pet_type_variable: service.pricing.variablePrice!.variables.petTypeVariable,
                                  pet_size_variable: service.pricing.variablePrice!.variables.petSizeVariable,
                                  hair_type_variable: service.pricing.variablePrice!.variables.hairTypeVariable,
                                  hair_length_variable: service.pricing.variablePrice!.variables.hairLengthVariable,
                                  exotic_type_variable: service.pricing.variablePrice!.variables.exoticTypeVariable
                              },
                              prices: service.pricing.variablePrice!.prices.map(vpo => ({
                                  uuid: vpo.uuid,
                                  pet_type_id: vpo.petType?.id,
                                  size_meta_variable: vpo.petSize ? vpo.petSize.id : undefined,
                                  hair_type_meta_variable: vpo.hairType ? vpo.hairType.id : undefined,
                                  hair_length_meta_variable: vpo.hairLength ? vpo.hairLength?.id : undefined,
                                  exotic_type_meta_variable: vpo.exoticType ? vpo.exoticType?.id : undefined,
                                  price: vpo.price
                              }))
                          }),
                business_area: service.businessArea?.id || '',
                pet_list: service.petTypes.map(petType => petType.id),
                //staff_ids: service.staff.map(staff => staff.id), //Comentado por bulk
                status_id: service.status.id,
                tag_list: service.tags,
                has_report: service.hasReport,
                house_call: service.houseCall,
                olb_enabled: service.olbEnabled,
                form_ids: Array.from(new Set((service.forms || []).map(form => form.id))),
                color: service.color,
                ...(applyNewPriceToAppointments !== undefined
                    ? { apply_new_price_to_appointments: applyNewPriceToAppointments }
                    : {}),
                evaluation_attributes: service.evaluation
                    ? {
                          id: service.evaluation?.id,
                          evaluation_expiry_enabled: service.evaluation.evaluationExpiry.enabled,
                          evaluation_expiry: service.evaluation.evaluationExpiry.enabled
                              ? service.evaluation.evaluationExpiry.duration
                              : null,
                          category_list: service.evaluation.category,
                          custom_question_enabled: service.evaluation.evaluationQuestionsEnabled,
                          custom_questions_attributes: [
                              ...(service.evaluation.evaluationQuestions?.map(q => ({
                                  id: q?.id,
                                  question_text: q.questionText,
                                  enabled: q.enabled,
                                  required: q.required,
                                  category: 'for_pets',
                                  display_order: q.displayOrder,
                                  questionable_type: q.questionableType,
                                  marketplace_id: marketplaceId,
                                  questionable_id: service.evaluation?.id
                              })) ?? []),
                              ...((deletedQuestionIds || []).map(id => ({
                                  id,
                                  _destroy: true,
                                  marketplace_id: marketplaceId
                              })) as any[])
                          ]
                      }
                    : null,
                // Daycare-specific fields (only included if present)
                ...(service.dropOffTime && {
                    drop_off_start: service.dropOffTime.start,
                    drop_off_end: service.dropOffTime.end
                }),
                ...(service.pickUpTime && {
                    pick_up_start: service.pickUpTime.start,
                    pick_up_end: service.pickUpTime.end
                }),
                // Boarding-specific fields (only included if present)
                ...(service.checkInTime && {
                    check_in_start: service.checkInTime.start,
                    check_in_end: service.checkInTime.end
                }),
                ...(service.checkOutTime && {
                    check_out_start: service.checkOutTime.start,
                    check_out_end: service.checkOutTime.end
                }),
                ...(service.businessArea?.id !== SERVICES_BUSINESS_AREAS.BOARDING && {
                    service_items_attributes: (service.serviceItems || []).map(si => ({
                        ...(si.id ? { id: si.id } : {}),
                        marketplace_id: marketplaceId,
                        item_id: si.itemID,
                        item_type: mapServiceItemTypeToApi(si.itemType),
                        business_area: si.businessArea, // Include business_area field
                        uuid: si.uuid,
                        amount: si.amount,
                        quantity: si.quantity,
                        per_stay: si.perStay,
                        _destroy: si._destroy ? si._destroy : false
                    }))
                }),
                marketplaces: service.marketplaces.map(mp => ({
                    marketplace_id: mp.marketplaceId,
                    tax_ids: mp.taxes,
                    staff_ids: mp.staffIds,
                    room_ids: mp.roomIds ?? [],
                    room_unit_ids: mp.roomUnitIds ?? [],
                    vehicle_ids: mp.vehicleIds ?? []
                }))
            };

            const serviceName = { ...serviceNoName, name: service.name };

            const serviceToSend = service.id !== undefined && changedName === false ? serviceNoName : serviceName;

            const response: AxiosResponse<ServiceDto> = await apiClientMethod(
                url,
                {
                    service: serviceToSend
                },
                createTokenConfig(store.getState().login.auth.token!)
            );

            try {
                await syncServiceFormIds(service, response.data);
            } catch (e) {
                dispatch(showError('Service saved, but forms could not be associated.'));
            }

            dispatch(saveBulkSuccess());
            // One-shot: CTA Saved Service while services onboarding incomplete
            try {
                const teamDone = store.getState().onboardingSteps.onboardingSteps.servicesDone; // variable naming kept
                const marketplaceId = store.getState().marketplace.marketplace?.id;
                if (!teamDone && marketplaceId) {
                    const key = `event_one_shot_${Events.SAVE_SERVICE_CLICKED}_${marketplaceId}`;
                    if (localStorage.getItem(key) !== '1') {
                        /*
                        Analytics.track(Events.SAVE_SERVICE_CLICKED as any, {
                            marketplace_id: marketplaceId,
                            service_id: saved.id,
                            business_area: saved.businessArea?.id,
                            pet_type_ids: saved.petTypes.map(pt => pt.id),
                            has_variable_price: !!saved.pricing.variablePrice,
                            has_variable_duration: !isServiceDuration(saved.duration),
                            staff_count: saved.staff.length
                        });*/
                        localStorage.setItem(key, '1');
                    }
                }
            } catch (e) {
                // non-blocking
            }
        } catch (apiError: any) {
            if (isCancelled(apiError)) return;
            dispatch(error());
            const defaultMsg = service.id === undefined ? 'Error creating service.' : 'Error saving service.';
            const res = apiError.response;

            if (!res) {
                dispatch(showError(defaultMsg));
                dispatch(fetchServicesThunk());
                return;
            }

            if (res.status === 422) {
                const nameErr = res.data?.name;
                const msg =
                    (Array.isArray(nameErr) && nameErr[0] && `Service name ${nameErr[0]}`) ||
                    parseValidationErrors(res.data) ||
                    defaultMsg;
                dispatch(showError(msg));
                return;
            }

            // Generic error message
            dispatch(showError(defaultMsg));
            //dispatch(fetchServicesThunk());
        }
    };
};

export const deleteServicesThunk = (
    serviceIds: Array<number>
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(deleteStart());

        const marketplaceId = store.getState().login.auth.marketplaceId;
        const url = `${deleteServicesUrl}?marketplace_id=${marketplaceId}&ids=${serviceIds}`;

        try {
            await ApiClient.delete(url, createTokenConfig(store.getState().login.auth.token!));
            dispatch(deleteSuccess());
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error deleting services.'));
            }
        }
        dispatch(fetchServicesThunk());
    };
};

export const activateServicesThunk = (
    serviceIds: Array<number>,
    active: boolean,
    onSuccessCallback?: () => void // Callback customized
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(active ? activateStart() : inactivateStart());

        const marketplaceId = store.getState().login.auth.marketplaceId;
        const url = `${activeServicesUrl}?marketplace_id=${marketplaceId}`;

        try {
            await ApiClient.post(
                url,
                {
                    ids: serviceIds,
                    status_id: active ? Status.ACTIVE : Status.INACTIVE
                },
                createTokenConfig(store.getState().login.auth.token!)
            );
            dispatch(active ? activateSuccess() : inactivateSuccess());
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error activating services.'));
            }
        }

        // Use custom callback or default behavior
        if (onSuccessCallback) {
            onSuccessCallback();
        } else {
            dispatch(fetchServicesThunk()); // Default behavior for grooming
        }
        if (active) {
            dispatch(showSuccess('Service activated successfully.'));
        } else {
            dispatch(showSuccess('Service deactivated successfully.'));
        }
    };
};

export const updatePetTypesServicesThunk = (
    serviceIds: Array<number>,
    petTypeIds: Array<string>
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(petTypesUpdateStart());

        const marketplaceId = store.getState().login.auth.marketplaceId;
        const url = `${petTypesUpdateServicesUrl}?marketplace_id=${marketplaceId}`;

        try {
            await ApiClient.post(
                url,
                {
                    ids: serviceIds,
                    list: petTypeIds
                },
                createTokenConfig(store.getState().login.auth.token!)
            );
            dispatch(petTypesUpdateSuccess());
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error updating pet types.'));
            }
        }
        dispatch(fetchServicesThunk());
    };
};

export const updateBusinessAreaServicesThunk = (
    serviceIds: Array<number>,
    businessAreaId: string
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(businessAreaUpdateStart());

        const marketplaceId = store.getState().login.auth.marketplaceId;
        const url = `${businessAreaUpdateServicesUrl}?marketplace_id=${marketplaceId}`;

        try {
            await ApiClient.post(
                url,
                {
                    ids: serviceIds,
                    service: businessAreaId
                },
                createTokenConfig(store.getState().login.auth.token!)
            );
            dispatch(businessAreaUpdateSuccess());
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error updating business area.'));
            }
        }
        dispatch(fetchServicesThunk());
    };
};

export const updateStaffServicesThunk = (
    serviceId: number,
    staffIds: Array<number>
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(staffUpdateStart());
        const marketplaceId = store.getState().login.auth.marketplaceId;
        const url = `${servicesUrl}/${serviceId}?marketplace_id=${marketplaceId}`;

        try {
            await ApiClient.patch(
                url,
                {
                    service: {
                        staff_ids: staffIds
                    }
                },
                createTokenConfig(store.getState().login.auth.token!)
            );
            dispatch(staffUpdateSuccess());
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error updating business area.'));
            }
        }
        dispatch(fetchServicesThunk());
    };
};

export const fetchDaycareServicesThunk = (
    petId: number,
    marketplaceId?: number
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(fetchDaycareServicesStart());

        const state = store.getState();
        const { marketplaceId: loginMarketplaceId, token } = state.login.auth;
        const selectedMarketplaceId = marketplaceId ?? loginMarketplaceId;

        const url = `${daycareServicesUrl}/${petId}?marketplace_id=${selectedMarketplaceId}`;

        try {
            const response: AxiosResponse<{ services: Array<DaycareServiceDto> }> = await ApiClient.get(
                url,
                createTokenConfig(token!)
            );
            dispatch(fetchDaycareServicesSuccess(response.data?.services));
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error fetching daycare services.'));
            }
        }
    };
};

export const fetchBoardingServicesThunk = (
    petId: number,
    marketplaceId?: number
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(fetchBoardingServicesStart());

        const state = store.getState();
        const { marketplaceId: loginMarketplaceId, token } = state.login.auth;
        const selectedMarketplaceId = marketplaceId ?? loginMarketplaceId;

        const url = `${boardingServicesUrl}/${petId}?marketplace_id=${selectedMarketplaceId}`;

        try {
            const response: AxiosResponse<{ services: Array<BoardingServiceDto> }> = await ApiClient.get(
                url,
                createTokenConfig(token!)
            );
            dispatch(fetchBoardingServicesSuccess(response.data?.services));
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error fetching boarding services.'));
            }
        }
    };
};

export const fetchBoardingOffersThunk = (
    petId: number,
    startDate: string,
    endDate: string,
    marketplaceId?: number
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        if (
            !Number.isFinite(petId) ||
            petId <= 0 ||
            !isValidBoardingOfferDateString(startDate) ||
            !isValidBoardingOfferDateString(endDate)
        ) {
            return;
        }

        dispatch(fetchBoardingOffersStart());

        const state = store.getState();
        const { marketplaceId: loginMarketplaceId, token } = state.login.auth;
        const selectedMarketplaceId = marketplaceId ?? loginMarketplaceId;

        const url = `${boardingOffersUrl}/${petId}?marketplace_id=${selectedMarketplaceId}&start_date=${startDate}&end_date=${endDate}`;

        try {
            const response: AxiosResponse<{ services: Array<BoardingServiceDto> }> = await ApiClient.get(
                url,
                createTokenConfig(token!)
            );
            dispatch(fetchBoardingOffersSuccess(response.data?.services));
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error fetching boarding services.'));
            }
        }
    };
};

export const fetchTransportationServicesThunk = (
    petId: number,
    marketplaceId?: number
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(fetchTransportationServicesStart());

        const state = store.getState();
        const { marketplaceId: loginMarketplaceId, token } = state.login.auth;
        const selectedMarketplaceId = marketplaceId ?? loginMarketplaceId;

        const url = `${transportationServicesUrl}/${petId}?marketplace_id=${selectedMarketplaceId}`;

        try {
            const response: AxiosResponse<{ services: Array<TransportationServiceDto> }> = await ApiClient.get(
                url,
                createTokenConfig(token!)
            );

            dispatch(fetchTransportationServicesSuccess(response.data?.services));
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error fetching transportation services.'));
            }
        }
    };
};

export const fetchTrainingServicesThunk = (
    petId: number,
    marketplaceId?: number
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(fetchTrainingServicesStart());

        const state = store.getState();
        const { marketplaceId: loginMarketplaceId, token } = state.login.auth;
        const selectedMarketplaceId = marketplaceId ?? loginMarketplaceId;

        const url = `${trainingServicesUrl}/${petId}?marketplace_id=${selectedMarketplaceId}`;

        try {
            const response: AxiosResponse<{ services: Array<TrainingServiceDto> }> = await ApiClient.get(
                url,
                createTokenConfig(token!)
            );

            dispatch(fetchTrainingServicesSuccess(response.data?.services));
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error fetching training services.'));
            }
        }
    };
};

export const fetchWalkingServicesThunk = (
    petId: number,
    marketplaceId?: number
): ThunkAction<void, ServicesState, null, ServicesAction | NotificationsAction> => {
    return async dispatch => {
        dispatch(fetchWalkingServicesStart());

        const state = store.getState();
        const { marketplaceId: loginMarketplaceId, token } = state.login.auth;
        const selectedMarketplaceId = marketplaceId ?? loginMarketplaceId;

        const url = `${walkingServicesUrl}/${petId}?marketplace_id=${selectedMarketplaceId}`;

        try {
            const response: AxiosResponse<{ services: Array<WalkingServiceDto> }> = await ApiClient.get(
                url,
                createTokenConfig(token!)
            );

            dispatch(fetchWalkingServicesSuccess(response.data?.services));
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error fetching walking services.'));
            }
        }
    };
};

export const fetchEvaluationServiceThunk = (): ThunkAction<
    void,
    RootState,
    null,
    ServicesAction | NotificationsAction
> => {
    return async dispatch => {
        dispatch(fetchEvaluationStart());
        const currentMarketplaceId = store.getState().login.auth.marketplaceId;
        const url = `${evaluationUrl}?marketplace_id=${currentMarketplaceId}`;

        try {
            const response: AxiosResponse<ServiceDto | null> = await ApiClient.get(
                url,
                createTokenConfig(store.getState().login.auth.token!)
            );

            // Convert and dispatch evaluation service
            const evaluationService = response.data ? convertToEvaluationService(response.data) : null;
            dispatch(fetchEvaluationSuccess(evaluationService));
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error());
                dispatch(showError('Error fetching evaluation service.'));
            }
        }
    };
};

export const reset = (): ServicesAction => {
    return {
        type: SERVICES_RESET_ACTION_TYPE
    };
};

const clear = (): ServicesAction => {
    return {
        type: SERVICES_CLEAR_ACTION_TYPE
    };
};

const fetchStart = (businessArea?: BusinessAreaType): ServicesAction => {
    return {
        type: SERVICES_FETCH_START_ACTION_TYPE,
        payload: businessArea ? { businessArea } : undefined
    };
};

export const fetchSuccess = (
    servicesPaginatedDto: ServicesPaginatedDto,
    duplicatedId?: number,
    businessArea?: BusinessAreaType
): ServicesAction => {
    return {
        type: SERVICES_FETCH_SUCCESS_ACTION_TYPE,
        payload: {
            services: servicesPaginatedDto.services.map(service => convertToService(service)),
            meta: convertToMeta(servicesPaginatedDto),
            duplicatedId,
            businessArea
        }
    };
};

const fetchEvaluationStart = (): ServicesAction => {
    return {
        type: SERVICES_FETCH_EVALUATION_START_ACTION_TYPE
    };
};

const fetchEvaluationSuccess = (evaluationService: Service | null): ServicesAction => {
    return {
        type: SERVICES_FETCH_EVALUATION_SUCCESS_ACTION_TYPE,
        payload: {
            evaluationService
        }
    };
};

const saveStart = (): ServicesAction => {
    return {
        type: SERVICES_SAVE_START_ACTION_TYPE
    };
};

const saveSuccess = (service: Service): ServicesAction => {
    return {
        type: SERVICES_SAVE_SUCCESS_ACTION_TYPE,
        payload: {
            service
        }
    };
};

const saveBulkStart = (): ServicesAction => {
    return {
        type: SERVICES_SAVE_BULK_START_ACTION_TYPE
    };
};

const saveBulkSuccess = (): ServicesAction => {
    return {
        type: SERVICES_SAVE_BULK_SUCCESS_ACTION_TYPE
    };
};

const deleteStart = (): ServicesAction => {
    return {
        type: SERVICES_DELETE_START_ACTION_TYPE
    };
};

const deleteSuccess = (): ServicesAction => {
    return {
        type: SERVICES_DELETE_SUCCESS_ACTION_TYPE
    };
};

const activateStart = (): ServicesAction => {
    return {
        type: SERVICES_ACTIVATE_START_ACTION_TYPE
    };
};

const activateSuccess = (): ServicesAction => {
    return {
        type: SERVICES_ACTIVATE_SUCCESS_ACTION_TYPE
    };
};

const inactivateStart = (): ServicesAction => {
    return {
        type: SERVICES_INACTIVATE_START_ACTION_TYPE
    };
};

const inactivateSuccess = (): ServicesAction => {
    return {
        type: SERVICES_INACTIVATE_SUCCESS_ACTION_TYPE
    };
};

const petTypesUpdateStart = (): ServicesAction => {
    return {
        type: SERVICES_PET_TYPES_UPDATE_START_ACTION_TYPE
    };
};

const petTypesUpdateSuccess = (): ServicesAction => {
    return {
        type: SERVICES_PET_TYPES_UPDATE_SUCCESS_ACTION_TYPE
    };
};

const businessAreaUpdateStart = (): ServicesAction => {
    return {
        type: SERVICES_BUSINESS_AREA_UPDATE_START_ACTION_TYPE
    };
};

const businessAreaUpdateSuccess = (): ServicesAction => {
    return {
        type: SERVICES_BUSINESS_AREA_UPDATE_SUCCESS_ACTION_TYPE
    };
};

const staffUpdateStart = (): ServicesAction => {
    return {
        type: SERVICES_STAFF_UPDATE_START_ACTION_TYPE
    };
};

const staffUpdateSuccess = (): ServicesAction => {
    return {
        type: SERVICES_STAFF_UPDATE_SUCCESS_ACTION_TYPE
    };
};

const fetchDaycareServicesStart = (): ServicesAction => {
    return {
        type: SERVICES_DAYCARE_FETCH_START_ACTION_TYPE
    };
};

const fetchDaycareServicesSuccess = (daycareServices: Array<DaycareServiceDto>): ServicesAction => {
    return {
        type: SERVICES_DAYCARE_FETCH_SUCCESS_ACTION_TYPE,
        payload: {
            daycareServices: convertToDaycareServices(daycareServices)
        }
    };
};

const fetchBoardingServicesStart = (): ServicesAction => {
    return {
        type: SERVICES_BOARDING_FETCH_START_ACTION_TYPE
    };
};

const fetchBoardingServicesSuccess = (boardingServices: Array<BoardingServiceDto>): ServicesAction => {
    return {
        type: SERVICES_BOARDING_FETCH_SUCCESS_ACTION_TYPE,
        payload: {
            boardingServices: convertToBoardingServices(boardingServices)
        }
    };
};

const fetchBoardingOffersStart = (): ServicesAction => {
    return {
        type: SERVICES_BOARDING_OFFERS_FETCH_START_ACTION_TYPE
    };
};

const fetchBoardingOffersSuccess = (boardingServices: Array<BoardingServiceDto>): ServicesAction => {
    return {
        type: SERVICES_BOARDING_OFFERS_FETCH_SUCCESS_ACTION_TYPE,
        payload: {
            boardingServices: convertToBoardingServices(boardingServices)
        }
    };
};

const fetchTransportationServicesStart = (): ServicesAction => ({
    type: SERVICES_TRANSPORTATION_FETCH_START_ACTION_TYPE
});

const fetchTransportationServicesSuccess = (
    transportationServices: Array<TransportationServiceDto>
): ServicesAction => ({
    type: SERVICES_TRANSPORTATION_FETCH_SUCCESS_ACTION_TYPE,
    payload: {
        transportationServices: convertToTransportationServices(transportationServices)
    }
});

const fetchTrainingServicesStart = (): ServicesAction => ({
    type: SERVICES_TRAINING_FETCH_START_ACTION_TYPE
});

const fetchTrainingServicesSuccess = (trainingServices: Array<TrainingServiceDto>): ServicesAction => ({
    type: SERVICES_TRAINING_FETCH_SUCCESS_ACTION_TYPE,
    payload: {
        trainingServices: convertToTrainingServices(trainingServices)
    }
});

const fetchWalkingServicesStart = (): ServicesAction => ({
    type: SERVICES_WALKING_FETCH_START_ACTION_TYPE
});

const fetchWalkingServicesSuccess = (walkingServices: Array<WalkingServiceDto>): ServicesAction => ({
    type: SERVICES_WALKING_FETCH_SUCCESS_ACTION_TYPE,
    payload: {
        walkingServices: convertToWalkingServices(walkingServices)
    }
});

const error = (): ServicesAction => {
    return {
        type: SERVICES_ERROR_ACTION_TYPE
    };
};

const mapStatusToIds = (status?: string): string => {
    switch (status) {
        case 'active':
            return '1,2';
        case 'inactive':
            return '3';
        case 'all':
            return '';
        default:
            return '';
    }
};
